// ============================================================
// Debuff System - Apply, Process, and Query Enemy Debuffs (v2.0)
// ============================================================
/** Maximum slow fraction (prevents total immobilization) */
const MAX_SLOW = 0.8;
/**
 * Apply a debuff to an enemy. If a debuff of the same type already exists,
 * refresh it — keeping the strongest value and resetting remaining time.
 * Bosses resist fear/stun/slow: duration and value reduced by 50%.
 */
export function applyDebuff(enemy, debuff) {
    let d = { ...debuff };
    // Bosses are now fully STUN IMMUNE — any stun application is dropped entirely.
    if (enemy.tier === 'boss' && d.type === 'stun')
        return;
    // Shadow-flavor bosses (undead/demon/void) are fully FEAR IMMUNE — they're already
    // dead, damned, or beyond mortal terror. Slow still applies (at 0.375×).
    if (enemy.tier === 'boss' && d.type === 'fear'
        && (enemy.faction === 'undead' || enemy.faction === 'demon' || enemy.faction === 'void')) {
        return;
    }
    if (enemy.tier === 'boss' && (d.type === 'fear' || d.type === 'slow')) {
        d.duration = Math.round(d.duration * 0.375);
        d.remaining = Math.round(d.remaining * 0.375);
        d.value = d.type === 'slow' ? d.value * 0.375 : d.value; // reduce slow strength too
    }
    // Faction resistances (non-boss — bosses already have their own resistance above)
    if (enemy.tier !== 'boss') {
        // Rot Bomber: -50% on every form of control (stun/fear/slow/root). This is a
        // damage-check mob — CC should delay the blast only modestly, not trivialise
        // it. Takes priority over faction rules (demon fear) so it doesn't double-dip.
        if (enemy.defId === 'rot_bomber' && (d.type === 'stun' || d.type === 'fear' || d.type === 'slow' || d.type === 'root')) {
            d.duration = Math.round(d.duration * 0.5);
            d.remaining = Math.round(d.remaining * 0.5);
            if (d.type === 'slow')
                d.value *= 0.5;
        }
        else {
            // Shadow Realm factions: -50% fear duration
            if (d.type === 'fear' && (enemy.faction === 'undead' || enemy.faction === 'demon' || enemy.faction === 'void')) {
                d.duration = Math.round(d.duration * 0.5);
                d.remaining = Math.round(d.remaining * 0.5);
            }
            // Minotaur (Ashen Labyrinth): -50% stun duration
            if (d.type === 'stun' && enemy.faction === 'minotaur') {
                d.duration = Math.round(d.duration * 0.5);
                d.remaining = Math.round(d.remaining * 0.5);
            }
        }
    }
    const existing = enemy.debuffs.find((e) => e.type === d.type);
    if (existing) {
        existing.value = Math.max(existing.value, d.value);
        existing.remaining = d.remaining;
        existing.duration = d.duration;
    }
    else {
        enemy.debuffs.push(d);
    }
}
/**
 * Tick all debuffs by deltaMs, removing any that have expired.
 * Call once per combat update tick.
 */
export function processDebuffs(enemy, deltaMs) {
    for (const debuff of enemy.debuffs) {
        debuff.remaining -= deltaMs;
    }
    enemy.debuffs = enemy.debuffs.filter((d) => d.remaining > 0);
}
/**
 * Get the effective movement speed of an enemy after applying all slow debuffs.
 * Feared enemies move backward (negative speed).
 * Stunned enemies don't move at all.
 */
export function getEffectiveSpeed(enemy) {
    // Stunned = no movement
    if (enemy.debuffs.some((d) => d.type === 'stun'))
        return 0;
    // Rooted = no movement (but attacks still go off — handled by the attack code, which only checks stun)
    if (enemy.debuffs.some((d) => d.type === 'root'))
        return 0;
    // Feared = move backward (return negative speed)
    if (enemy.debuffs.some((d) => d.type === 'fear'))
        return -enemy.speed * 0.5;
    const slows = enemy.debuffs.filter((d) => d.type === 'slow');
    if (slows.length === 0)
        return enemy.speed;
    const strongestSlow = Math.min(MAX_SLOW, Math.max(...slows.map((d) => d.value)));
    return enemy.speed * (1 - strongestSlow);
}
/**
 * Check if enemy is stunned.
 */
export function isStunned(enemy) {
    return enemy.debuffs.some((d) => d.type === 'stun');
}
/**
 * Get bonus spell damage multiplier on feared enemies.
 */
export function getBonusSpellDamageMult(enemy) {
    const fear = enemy.debuffs.find((d) => d.type === 'fear' || d.type === 'bonus_spell_damage');
    return fear ? 1 + fear.value : 1;
}
//# sourceMappingURL=debuffs.js.map